import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SegmentReviewCardComponent } from '../segment-review-card/segment-review-card.component';
import { SegmentPreviewComponent } from '../segment-preview/segment-preview.component';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { SegmentService } from '../../../../services/segments/segment-service.service';
import { GroupAudienceService } from '../../../../services/segments/group-audience.service';
import { IEntitySegmentQuery, builderCommands, isCompleteRule } from '../segment-utilities';

/** Step 3 of the segment builder: check, then create or save. */
@Component({
  selector: 'segment-review',
  standalone: true,
  imports: [SegmentReviewCardComponent, SegmentPreviewComponent],
  templateUrl: './segment-review.component.html',
  styleUrls: ['../segment-builder.css', './segment-review.component.css'],
})
export class SegmentReviewComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  route = inject(ActivatedRoute);
  contactService = inject(ContactsService);
  segmentService = inject(SegmentService);
  private audience = inject(GroupAudienceService);

  saving = false;
  errorMsg: string = '';

  // for when editing a segment
  selectedSegment: IEntitySegmentQuery | null = null;

  // getters
  get isEditView() {
    return this.router.url.includes('/segmented-list/edit');
  }

  get routeParamId() {
    return this.route.snapshot.paramMap.get('id');
  }

  get selectedSegmentId() {
    return this.selectedSegment?.id;
  }

  get actionButtontext() {
    return this.isEditView ? 'Save changes' : 'Create segment';
  }

  get rules() {
    return this.segmentService.draftRules;
  }

  /** What still blocks saving, in the order to fix it. */
  get blocker(): string {
    const info = this.segmentService.segmentInformation;
    if (!info.segmentName?.trim()) return 'Add a name in the first step before saving.';
    if (!info.description?.trim()) return 'Add a description in the first step before saving.';
    if (!this.rules.some(isCompleteRule)) return 'Add at least one condition before saving.';
    if (this.isEditView && this.selectedSegment?.uuid !== this.routeParamId) {
      return 'This page lost track of the segment you were editing. Go back to Segments and open it again.';
    }
    return '';
  }

  goToPrevious() {
    this.router.navigate(builderCommands(this.router.url, this.routeParamId), {
      queryParams: { title: 'rule' },
    });
  }

  cancel() {
    this.segmentService.clearSegmentInformation();
    this.router.navigate(['organization', 'contacts', 'segmented-list']);
  }

  createSegment() {
    if (this.saving) return;
    this.errorMsg = this.blocker;
    if (this.errorMsg) return;
    this.saving = true;

    if (!this.isEditView) {
      this.contactService.createSegment().subscribe({
        next: () => {
          this.saving = false;
          this.contactService.showSuccess('Segment created.');
          this.router.navigate(['organization', 'contacts', 'segmented-list']);
        },
        error: () => {
          this.saving = false;
          this.errorMsg = 'The segment was not created. Check your connection and try again.';
        },
      });
    } else {
      this.contactService.updateSegment(this.routeParamId!, this.selectedSegment!).subscribe({
        next: () => {
          this.saving = false;
          // New conditions, new members: drop the cached count.
          this.audience.invalidate({ kind: 'segment', uuid: this.routeParamId! });
          this.segmentService.clearSegmentInformation();
          this.contactService.showSuccess('Segment updated.');
          this.router.navigate(['organization', 'contacts', 'segmented-list']);
        },
        error: () => {
          this.saving = false;
          this.errorMsg = 'Your changes were not saved. Check your connection and try again.';
        },
      });
    }
  }

  ngOnInit() {
    this.segmentService.getSelectedSegment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (segment: IEntitySegmentQuery | null) => {
        this.selectedSegment = segment;
      },
    });
  }
}
